import axios from 'axios';
import { toast } from 'react-toastify';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_GETWAY_API+ "dif-auction/api/v1" || 'http://localhost:8626/dif-auction/api/v1';
const COMMON_GATEWAY_API = process.env.REACT_APP_GETWAY_API || 'https://stage-api.oto.com';

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        // Show success toast for non-GET requests (mutations)
        // Skip if skipToast is explicitly set to true in the request config
        if (response.config.method !== 'get' && response.data?.message && !response.config.skipToast) {
            // toast.success(response.data.message);
        } else if (response.config.method !== 'get' && response.status === 200) {
            // Some APIs might not return a message, but we still want to indicate success for mutations
            // toast.success('Action completed successfully'); 
        }
        return response.data;
    },
    (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        
        if (error.response?.status === 401) {
            // Token expired or invalid
            const isLoginPage = window.location.pathname === '/';
            if (!isLoginPage) {
                toast.error('Session expired. Please login again.');
                localStorage.clear();
                window.location.href = '/';
            }
        } else {
            toast.error(message);
        }
        
        return Promise.reject(error);
    }
);

const MasterService = {
    post: (url, data, config = {}) => {
        return apiClient.post(url, data, config);
    },

    get: (url, config = {}) => {
        return apiClient.get(url, config);
    },

    put: (url, data, config = {}) => {
        return apiClient.put(url, data, config);
    },

    delete: (url, config = {}) => {
        return apiClient.delete(url, config);
    },

    // Specialized methods for easy reuse
    login: (data) => {
        return MasterService.post('/auth/login', data);
    },

    logout: (userId) => {
        return MasterService.post('/auth/logout', { userId });
    },

    // File upload service
    uploadFilesToS3: (formData, headers = {}) => {
        const entity_source = localStorage.getItem('entity_source') || 'oto-in';
        const uploadHeaders = {
            ...headers,
            'tenant': entity_source
        };
        
        return axios.post(
            `${COMMON_GATEWAY_API}core/commonservice/docs_upload`,
            formData,
            { headers: uploadHeaders }
        );
    },

    // Save documents service
    saveDocuments: (documentData) => {
        return MasterService.post('/lead/save_documents', documentData);
    },

    // Remove documents service
    removeDocuments: (documentIds) => {
        return axios.post(`${API_BASE_URL}/lead/remove_documents`, {
            ids: documentIds
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Document configurations service
    getDocumentConfigs: () => {
        return MasterService.post('/lead/document_form_field', {});
    },

    // Get Make, Model, Variant data
    getMMV: () => {
        return axios.get(`${COMMON_GATEWAY_API}core/commonservice/mmv_all`);
    },

    // Get all dealers
    getDealers: () => {
        return axios.post(`${COMMON_GATEWAY_API}dealer/dealer/list-all`, {
            filter:{
                is_dealer_subscribe_for_dif:1
            }
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'e8fe3567-00cd-4121-b34b-495c797526a4'
            }
        });
    },

    // Get car colors
    getCarColors: () => {
        return axios.post(`${API_BASE_URL}/lead/car_colors`, {}, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Get dealers by city IDs
    getDealersByCity: (cityIds) => {
        return axios.post(`${COMMON_GATEWAY_API}dealer/dealer/list-all`, {
            filter: {
                // is_dealer_subscribe_for_dif:1,
                city_ids: cityIds
            }
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'e8fe3567-00cd-4121-b34b-495c797526a4'
            }
        });
    },

    // Get cities and states
    getCities: () => {
        return axios.get(`${COMMON_GATEWAY_API}core/commonservice/state_city_all`, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },

    // Get leads by dealer IDs
    getLeadsByDealers: (dealerIds) => {
        return axios.post(`${API_BASE_URL}/lead/leads_by_dealers`, {
            dealer_ids: dealerIds
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },
    
    getAuctionDetails: (auctionId) => {
        return axios.post(`${API_BASE_URL}/auction/auction-detail`, {
            auction_id: auctionId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Save vehicle details
    saveVehicleDetails: (vehicleData) => {
        return axios.post(`${API_BASE_URL}/lead/save`, vehicleData, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Get vehicle details for editing
    getVehicleDetails: (leadId, dealerId) => {
        return axios.post(`${API_BASE_URL}/lead/vehicle_details`, {
            lead_id: leadId,
            dealer_id: dealerId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Save documents
    saveDocuments: (documentData) => {
        return axios.post(`${API_BASE_URL}/lead/save_documents`, documentData, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Create auction
    createAuction: (auctionData) => {
        return axios.post(`${COMMON_GATEWAY_API}dif-auction/api/v1/auction/add-auction`, auctionData, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Update auction status
    updateAuctionStatus: (leadId, auctionStatus, remarks) => {
        return axios.post(`${API_BASE_URL}/lead/update_auction_status`, {
            lead_id: leadId,
            auction_status: auctionStatus,
            remarks: remarks
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    },

    // Update document paths
    updateDocumentPaths: (docId, docUrl) => {
        return axios.post(`${API_BASE_URL}/lead/update_document_paths`, {
            doc_id: docId,
            doc_url: docUrl
        }, {
            headers: {
                'Content-Type': 'application/json',
                'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
            }
        });
    }
};

export default MasterService;
